import React from "react";
import { motion } from "framer-motion";
import { useReveal } from "../../hooks/useReveal";
import { easing } from "../../lib/animations";
import { VelocityRow } from "./VelocityRow";

const Cross = ({ size = 110 }) => (
    <span
        aria-hidden="true"
        style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: size,
            height: size,
            borderRadius: 0,
            background: "#0c0c0c",
            color: "#fff",
            flexShrink: 0,
            marginLeft: 60,
            marginRight: 60,
        }}
    >
        <svg viewBox="0 0 40 40" fill="none" style={{ width: "50%", height: "50%" }}>
            <line x1="0" y1="20" x2="40" y2="20" stroke="currentColor" strokeWidth="2" />
        </svg>
    </span>
);

const TextWord = ({ children }) => (
    <span
        style={{
            fontFamily: "'Space Grotesk', Manrope, sans-serif",
            fontWeight: 700,
            fontSize: "clamp(100px, 16vw, 240px)",
            lineHeight: 0.86,
            letterSpacing: "-0.045em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            color: "#0c0c0c",
            paddingRight: 60,
        }}
    >
        {children}
    </span>
);

const ImgCell = ({ src, w = 620, h = 420 }) => {
    const [hover, setHover] = React.useState(false);
    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                width: w,
                height: h,
                overflow: "hidden",
                marginRight: 36,
                flexShrink: 0,
                background: "#1a1a1a",
                position: "relative",
            }}
        >
            <img
                src={src}
                alt=""
                loading="lazy"
                draggable={false}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transform: hover ? "scale(1.04)" : "scale(1)",
                    filter: hover
                        ? "grayscale(0.05) contrast(1.04) saturate(1.05)"
                        : "grayscale(0.85) contrast(1.04) brightness(0.92)",
                    transition:
                        "transform 1.8s cubic-bezier(0.22, 1, 0.36, 1), filter 1.6s cubic-bezier(0.22, 1, 0.36, 1)",
                }}
            />
        </div>
    );
};

export const PlayfulMinds = () => {
    const [ref, inViewRaw] = useReveal({ threshold: 0.15 });
    // PROOF Community: do not reverse when scrolling back up — keep the section in its
    // settled state once it has entered the viewport.
    const [inView, setInView] = React.useState(false);
    React.useEffect(() => {
        if (inViewRaw) setInView(true);
    }, [inViewRaw]);

    const textRow = (
        <>
            <TextWord>PROOF</TextWord>
            <Cross />
            <TextWord>COMMUNITY</TextWord>
            <Cross />
        </>
    );

    const imageRow = (
        <>
            <ImgCell src="/assets/yk/community/c1.png" />
            <ImgCell src="/assets/yk/community/c2.jpg" w={500} h={420} />
            <ImgCell src="/assets/yk/community/c3.png" />
            <ImgCell src="/assets/yk/community/c4.png" w={500} h={420} />
            <ImgCell src="/assets/yk/community/c1.png" />
            <ImgCell src="/assets/yk/community/c3.png" w={500} h={420} />
            <ImgCell src="/assets/yk/community/c2.jpg" />
        </>
    );

    return (
        <section
            ref={ref}
            className="playful-block"
            data-testid="playful-minds"
            style={{
                background: "var(--bg)",
                color: "var(--ink)",
                minHeight: "100vh",
                padding: "clamp(60px, 9vw, 140px) 0",
                overflow: "hidden",
                borderTop: "1px solid var(--line)",
                borderBottom: "1px solid var(--line)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "clamp(40px, 6vw, 96px)",
                position: "relative",
            }}
        >
            {/* Gentle black → white wipe overlay; barely noticeable */}
            <motion.div
                aria-hidden="true"
                initial={{ y: "0%" }}
                animate={inView ? { y: "-101%" } : { y: "0%" }}
                transition={{ duration: 1.6, ease: easing.soft, delay: 0.1 }}
                style={{
                    position: "absolute",
                    inset: 0,
                    background: "#0c0c0c",
                    zIndex: 5,
                    pointerEvents: "none",
                }}
            />

            {/* Top row: huge TEXT — fades in gently first, then marquee carries it */}
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                transition={{ duration: 1.1, ease: easing.primary, delay: 0.45 }}
            >
                <VelocityRow
                    baseVelocity={20}
                    direction={1}
                    scrollMultiplier={1}
                    copies={3}
                    data-testid="playful-text-row"
                >
                    {textRow}
                </VelocityRow>
            </motion.div>

            {/* Bottom row: images — fade in slightly after the text, then drift */}
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                transition={{ duration: 1.1, ease: easing.primary, delay: 0.75 }}
            >
                <VelocityRow
                    baseVelocity={15}
                    direction={-1}
                    scrollMultiplier={1}
                    copies={3}
                    data-testid="playful-images-row"
                >
                    {imageRow}
                </VelocityRow>
            </motion.div>
        </section>
    );
};

export default PlayfulMinds;
